/**
 * Utilisation assumptions editor — targets + formula weights.
 * Truth mode persists to zeecore /utilisation/v2/config; batch mode is local UI state only.
 */

import { useCallback, useEffect, useState } from 'react';
import { Save, Settings, X } from 'lucide-react';
import { Button } from '../ui/button';
import { getCurrentUserEmail, readSessionUserEmail } from '../../auth/cognitoAuth';
import {
  fetchTruthUtilisationConfig,
  saveTruthUtilisationConfig,
} from '../../services/utilisationTruthClient';
import {
  UTILISATION_TARGET_VEHICLE_TYPES,
  UTILISATION_TARGET_TONNAGE_CLASSES,
  DEFAULT_UTILISATION_TONNAGE_TARGETS,
  DEFAULT_UTILISATION_BODY_HOURS,
  type UtilisationAssumptionsConfig,
  type UtilisationTargetRateBasis,
} from '../../types/utilisationTruth';
import {
  componentWeightsToUi,
  defaultComponentWeights,
  inferWeightPreset,
  uiWeightsToComponent,
  type UtilisationWeightPreset,
  type UtilisationWeightsUi,
} from '../../utils/utilisationFormulaWeights';
import UtilisationFormulaWeightsSection from './UtilisationFormulaWeightsSection';
import ConfigMetaBadge from '../config/ConfigMetaBadge';
import { ConfigChangeHistoryButton } from '../config/ConfigChangeHistoryPanel';

export interface UtilisationAssumptionsPanelProps {
  visible: boolean;
  onClose?: () => void;
  onSaved?: () => void;
  className?: string;
  /** Truth layer save vs client-only batch CSV recompute. */
  mode?: 'truth' | 'batch';
  serviceType?: 'logistics' | 'emergency_services';
  /** Batch mode: controlled formula weights. */
  batchWeights?: UtilisationWeightsUi;
  batchPreset?: UtilisationWeightPreset;
  onBatchWeightsChange?: (preset: UtilisationWeightPreset, weights: UtilisationWeightsUi) => void;
}

export default function UtilisationAssumptionsPanel({
  visible,
  onClose,
  onSaved,
  className = '',
  mode = 'truth',
  serviceType = 'logistics',
  batchWeights,
  batchPreset = 'balanced',
  onBatchWeightsChange,
}: UtilisationAssumptionsPanelProps) {
  const emergency = serviceType === 'emergency_services';
  const truthMode = mode === 'truth';

  const [configData, setConfigData] = useState<UtilisationAssumptionsConfig | null>(null);
  const [configDraft, setConfigDraft] = useState<UtilisationAssumptionsConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configEditMode, setConfigEditMode] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  const [weightsUi, setWeightsUi] = useState<UtilisationWeightsUi>(() =>
    componentWeightsToUi(defaultComponentWeights(emergency), emergency),
  );
  const [weightPreset, setWeightPreset] = useState<UtilisationWeightPreset>('balanced');

  const syncWeightsFromDraft = useCallback(
    (draft: UtilisationAssumptionsConfig | null) => {
      const cw =
        draft?.utilisation_analysis.component_weights ?? defaultComponentWeights(emergency);
      const ui = componentWeightsToUi(cw, emergency);
      setWeightsUi(ui);
      setWeightPreset(inferWeightPreset(ui, emergency));
    },
    [emergency],
  );

  const fetchConfig = useCallback(async () => {
    setConfigLoading(true);
    setConfigError(null);
    try {
      const data = await fetchTruthUtilisationConfig();
      setConfigData(data);
      const cloned = data ? JSON.parse(JSON.stringify(data)) : null;
      setConfigDraft(cloned);
      syncWeightsFromDraft(cloned);
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Failed to load utilisation assumptions');
    } finally {
      setConfigLoading(false);
    }
  }, [syncWeightsFromDraft]);

  useEffect(() => {
    if (visible && truthMode && !configData && !configLoading) {
      void fetchConfig();
    }
  }, [visible, truthMode, configData, configLoading, fetchConfig]);

  useEffect(() => {
    if (!visible) {
      setConfigEditMode(false);
    }
  }, [visible]);

  useEffect(() => {
    if (!truthMode && batchWeights) {
      setWeightsUi(batchWeights);
      setWeightPreset(batchPreset);
    }
  }, [truthMode, batchWeights, batchPreset]);

  const applyWeightsToDraft = useCallback(
    (preset: UtilisationWeightPreset, ui: UtilisationWeightsUi) => {
      setWeightsUi(ui);
      setWeightPreset(preset);
      if (truthMode) {
        setConfigDraft((prev) =>
          prev
            ? {
                ...prev,
                utilisation_analysis: {
                  ...prev.utilisation_analysis,
                  component_weights: uiWeightsToComponent(ui, emergency),
                },
              }
            : prev,
        );
      } else {
        onBatchWeightsChange?.(preset, ui);
      }
    },
    [truthMode, emergency, onBatchWeightsChange],
  );

  const saveConfig = useCallback(async () => {
    if (!configDraft) return;
    setConfigSaving(true);
    setConfigError(null);
    try {
      const actor =
        readSessionUserEmail()
        || (await getCurrentUserEmail())?.trim()
        || 'user';
      const payload: UtilisationAssumptionsConfig = {
        ...configDraft,
        utilisation_analysis: {
          ...configDraft.utilisation_analysis,
          component_weights: uiWeightsToComponent(weightsUi, emergency),
        },
        updated_by: actor,
        updated_at: new Date().toISOString(),
      };
      const saved = await saveTruthUtilisationConfig(payload);
      setConfigData(saved);
      const cloned = JSON.parse(JSON.stringify(saved)) as UtilisationAssumptionsConfig;
      setConfigDraft(cloned);
      syncWeightsFromDraft(cloned);
      setConfigEditMode(false);
      onSaved?.();
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Failed to save utilisation assumptions');
    } finally {
      setConfigSaving(false);
    }
  }, [configDraft, weightsUi, emergency, onSaved, syncWeightsFromDraft]);

  const beginEdit = () => {
    if (configEditMode) return;
    setConfigDraft(configData ? JSON.parse(JSON.stringify(configData)) : null);
    syncWeightsFromDraft(configData);
    setConfigEditMode(true);
  };

  const cancelEdit = () => {
    const cloned = configData ? (JSON.parse(JSON.stringify(configData)) as UtilisationAssumptionsConfig) : null;
    setConfigDraft(cloned);
    syncWeightsFromDraft(cloned);
    setConfigEditMode(false);
  };

  const ua = configDraft?.utilisation_analysis;
  const weightsDisabled = truthMode ? !configEditMode : false;
  const targetBasis: UtilisationTargetRateBasis = ua?.target_rate_basis ?? 'body_style';
  const rateKeys =
    targetBasis === 'tonnage'
      ? UTILISATION_TARGET_TONNAGE_CLASSES
      : UTILISATION_TARGET_VEHICLE_TYPES;

  const mileageMap =
    targetBasis === 'tonnage'
      ? (ua?.mileage_target_miles_tonnage ?? DEFAULT_UTILISATION_TONNAGE_TARGETS.mileage_target_miles_tonnage)
      : (ua?.mileage_target_miles ?? {});
  const revenueMap =
    targetBasis === 'tonnage'
      ? (ua?.revenue_target_gbp_tonnage ?? DEFAULT_UTILISATION_TONNAGE_TARGETS.revenue_target_gbp_tonnage)
      : (ua?.revenue_target_gbp ?? {});
  const loadKgMap =
    targetBasis === 'tonnage'
      ? (ua?.load_capacity_kg_tonnage ?? DEFAULT_UTILISATION_TONNAGE_TARGETS.load_capacity_kg_tonnage)
      : (ua?.load_capacity_kg ?? {});

  const coerceHoursMap = (
    value: number | Record<string, number> | undefined,
    fallback: Record<string, number>,
  ): Record<string, number> => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      const out: Record<string, number> = { ...fallback };
      for (const k of Object.keys(out)) out[k] = value;
      out.default = value;
      out.Default = value;
      return out;
    }
    if (value && typeof value === 'object') return { ...fallback, ...value };
    return { ...fallback };
  };

  const drivingMap =
    targetBasis === 'tonnage'
      ? coerceHoursMap(
          ua?.target_driving_hours_tonnage,
          DEFAULT_UTILISATION_TONNAGE_TARGETS.target_driving_hours_tonnage,
        )
      : coerceHoursMap(ua?.target_driving_hours, DEFAULT_UTILISATION_BODY_HOURS.target_driving_hours);
  const waitingMap =
    targetBasis === 'tonnage'
      ? coerceHoursMap(
          ua?.target_waiting_hours_tonnage,
          DEFAULT_UTILISATION_TONNAGE_TARGETS.target_waiting_hours_tonnage,
        )
      : coerceHoursMap(ua?.target_waiting_hours, DEFAULT_UTILISATION_BODY_HOURS.target_waiting_hours);

  const readTarget = (map: Record<string, number>, key: string): number =>
    map[key] ?? map.Default ?? map.default ?? 0;

  /** Capacity is stored in kg; operators edit tonnes (T). */
  const kgToTonnes = (kg: number): number => Math.round((kg / 1000) * 1000) / 1000;
  const tonnesToKg = (t: number): number => Math.round(t * 1000);

  const setTargetMapValue = (
    field:
      | 'mileage_target_miles'
      | 'revenue_target_gbp'
      | 'load_capacity_kg'
      | 'mileage_target_miles_tonnage'
      | 'revenue_target_gbp_tonnage'
      | 'load_capacity_kg_tonnage'
      | 'target_driving_hours'
      | 'target_waiting_hours'
      | 'target_driving_hours_tonnage'
      | 'target_waiting_hours_tonnage',
    key: string,
    value: number,
  ) => {
    setConfigDraft((prev) => {
      if (!prev) return prev;
      const raw = prev.utilisation_analysis[field];
      const current =
        typeof raw === 'number'
          ? Object.fromEntries(rateKeys.map((k) => [k, raw]))
          : ((raw as Record<string, number> | undefined) ?? {});
      return {
        ...prev,
        utilisation_analysis: {
          ...prev.utilisation_analysis,
          [field]: { ...current, [key]: value },
        },
      };
    });
  };

  const mileageField =
    targetBasis === 'tonnage' ? 'mileage_target_miles_tonnage' : 'mileage_target_miles';
  const revenueField =
    targetBasis === 'tonnage' ? 'revenue_target_gbp_tonnage' : 'revenue_target_gbp';
  const loadField =
    targetBasis === 'tonnage' ? 'load_capacity_kg_tonnage' : 'load_capacity_kg';
  const drivingField =
    targetBasis === 'tonnage' ? 'target_driving_hours_tonnage' : 'target_driving_hours';
  const waitingField =
    targetBasis === 'tonnage' ? 'target_waiting_hours_tonnage' : 'target_waiting_hours';

  if (!visible) return null;

  return (
    <div className={`bg-card border border-chart-10/30 rounded-lg overflow-hidden ${className}`}>
      <div className="flex items-center justify-between px-4 py-2.5 bg-chart-10/10 border-b border-chart-10/30">
        <div className="flex items-center gap-2 min-w-0">
          <Settings className="w-3.5 h-3.5 text-chart-10 shrink-0" />
          <span className="text-xs font-semibold text-foreground">Utilisation Assumptions</span>
          {truthMode && configData ? (
            <ConfigMetaBadge variant="version" version={configData.version} className="ml-2" />
          ) : null}
          {!truthMode && (
            <span className="text-xs font-medium text-muted-foreground ml-2">Batch mode — weights apply locally</span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {truthMode && (
            <>
              {!configEditMode ? (
                <Button
                  type="button"
                  variant="default"
                  onClick={() => beginEdit()}
                  disabled={!configDraft}
                  className="h-auto px-2.5 py-1 text-xs bg-chart-10 hover:bg-chart-10 text-white disabled:bg-muted disabled:opacity-100"
                >
                  Edit All
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    variant="default"
                    onClick={() => void saveConfig()}
                    disabled={configSaving}
                    className="h-auto px-2.5 py-1 text-xs disabled:bg-muted disabled:opacity-100"
                  >
                    <Save className="w-3 h-3" />
                    {configSaving ? 'Saving…' : 'Save'}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={cancelEdit}
                    className="h-auto px-2.5 py-1 text-xs"
                  >
                    Cancel
                  </Button>
                </>
              )}
            </>
          )}
          {onClose && (
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              onClick={onClose}
              className="p-1"
              aria-label="Close assumptions panel"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      {configError && (
        <div className="px-4 py-2 text-xs text-destructive bg-destructive/20 border-b border-destructive/20">
          {configError}
        </div>
      )}

      {truthMode && configLoading ? (
        <div className="px-4 py-6 text-center text-xs text-muted-foreground">Loading utilisation targets…</div>
      ) : truthMode && ua && configDraft ? (
        <div className="p-4 space-y-6">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="min-w-56 max-w-md flex-1">
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Apply targets by
              </label>
              {configEditMode ? (
                <select
                  value={targetBasis}
                  onChange={(e) =>
                    setConfigDraft((prev) =>
                      prev
                        ? {
                            ...prev,
                            utilisation_analysis: {
                              ...prev.utilisation_analysis,
                              target_rate_basis: e.target.value as UtilisationTargetRateBasis,
                              mileage_target_miles_tonnage:
                                prev.utilisation_analysis.mileage_target_miles_tonnage
                                ?? { ...DEFAULT_UTILISATION_TONNAGE_TARGETS.mileage_target_miles_tonnage },
                              revenue_target_gbp_tonnage:
                                prev.utilisation_analysis.revenue_target_gbp_tonnage
                                ?? { ...DEFAULT_UTILISATION_TONNAGE_TARGETS.revenue_target_gbp_tonnage },
                              load_capacity_kg_tonnage:
                                prev.utilisation_analysis.load_capacity_kg_tonnage
                                ?? { ...DEFAULT_UTILISATION_TONNAGE_TARGETS.load_capacity_kg_tonnage },
                              target_driving_hours_tonnage:
                                prev.utilisation_analysis.target_driving_hours_tonnage
                                ?? { ...DEFAULT_UTILISATION_TONNAGE_TARGETS.target_driving_hours_tonnage },
                              target_waiting_hours_tonnage:
                                prev.utilisation_analysis.target_waiting_hours_tonnage
                                ?? { ...DEFAULT_UTILISATION_TONNAGE_TARGETS.target_waiting_hours_tonnage },
                            },
                          }
                        : prev,
                    )
                  }
                  className="w-full bg-background border border-border rounded px-2 py-1.5 text-xs text-foreground focus:ring-chart-10 focus:border-chart-10"
                >
                  <option value="body_style">Body style (Lorry, Tractor Unit, …)</option>
                  <option value="tonnage">Tonnage class (3.5T, 7.5T, 18T, 26T, Artic)</option>
                </select>
              ) : (
                <div className="w-full text-left bg-background/40 border border-border rounded px-2 py-1.5 text-xs text-foreground">
                  {targetBasis === 'tonnage'
                    ? 'Tonnage class (3.5T, 7.5T, 18T, 26T, Artic)'
                    : 'Body style (Lorry, Tractor Unit, …)'}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Must match Assets → Vehicle Details.
              </p>
            </div>
            <p className="text-xs text-muted-foreground pb-1">
              {configEditMode
                ? 'Edit targets below, then Save.'
                : 'Use Edit All to change targets.'}
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-xs text-foreground">
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th className="text-left py-2 pr-3 font-semibold">
                    {targetBasis === 'tonnage' ? 'Tonnage class' : 'Vehicle type'}
                  </th>
                  <th className="text-right py-2 px-2 font-semibold">Load capacity (T)</th>
                  <th className="text-right py-2 px-2 font-semibold">Miles / day</th>
                  <th className="text-right py-2 px-2 font-semibold">Revenue / day (£)</th>
                  <th className="text-right py-2 px-2 font-semibold">Driving hrs / day</th>
                  <th className="text-right py-2 pl-2 font-semibold">Waiting hrs / day</th>
                </tr>
              </thead>
              <tbody>
                {rateKeys.map((vt) => {
                  const miles = readTarget(mileageMap as Record<string, number>, vt);
                  const revenue = readTarget(revenueMap as Record<string, number>, vt);
                  const loadT = kgToTonnes(readTarget(loadKgMap as Record<string, number>, vt));
                  const driving = readTarget(drivingMap, vt);
                  const waiting = readTarget(waitingMap, vt);
                  const inputClass =
                    'w-20 bg-background border border-border rounded px-2 py-1 text-right text-foreground focus:ring-chart-10 focus:border-chart-10';
                  const editable = (
                    value: number,
                    display: string | number,
                    onChange: (n: number) => void,
                    step: string,
                    widthClass = 'w-20',
                  ) =>
                    configEditMode ? (
                      <input
                        type="number"
                        step={step}
                        min="0"
                        value={value}
                        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
                        className={`${inputClass} ${widthClass}`}
                      />
                    ) : (
                      <span className="tabular-nums text-foreground">{display}</span>
                    );
                  return (
                    <tr key={vt} className="border-b border-border/60">
                      <td className="py-2 pr-3 font-medium text-foreground">{vt}</td>
                      <td className="py-2 px-2 text-right">
                        {editable(
                          loadT,
                          loadT,
                          (n) => setTargetMapValue(loadField, vt, tonnesToKg(n)),
                          '0.1',
                          'w-24',
                        )}
                      </td>
                      <td className="py-2 px-2 text-right">
                        {editable(
                          miles,
                          miles,
                          (n) => setTargetMapValue(mileageField, vt, n),
                          '1',
                        )}
                      </td>
                      <td className="py-2 px-2 text-right">
                        {editable(
                          revenue,
                          revenue,
                          (n) => setTargetMapValue(revenueField, vt, n),
                          '1',
                        )}
                      </td>
                      <td className="py-2 px-2 text-right">
                        {editable(
                          driving,
                          driving,
                          (n) => setTargetMapValue(drivingField, vt, n),
                          '0.5',
                        )}
                      </td>
                      <td className="py-2 pl-2 text-right">
                        {editable(
                          waiting,
                          waiting,
                          (n) => setTargetMapValue(waitingField, vt, n),
                          '0.5',
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <UtilisationFormulaWeightsSection
            serviceType={serviceType}
            weights={weightsUi}
            preset={weightPreset}
            disabled={weightsDisabled}
            onPresetChange={applyWeightsToDraft}
            onWeightsChange={(ui) => applyWeightsToDraft('custom', ui)}
          />
        </div>
      ) : truthMode ? (
        <div className="px-4 py-6 text-center text-xs text-muted-foreground">
          No utilisation config found. Run{' '}
          <code className="text-chart-10">npm run seed:utilisation-config -- --org &lt;orgId&gt;</code> in zeecore.
        </div>
      ) : (
        <div className="p-4">
          <UtilisationFormulaWeightsSection
            serviceType={serviceType}
            weights={weightsUi}
            preset={weightPreset}
            disabled={weightsDisabled}
            onPresetChange={applyWeightsToDraft}
            onWeightsChange={(ui) => applyWeightsToDraft('custom', ui)}
          />
        </div>
      )}

      {truthMode ? (
        <div className="px-4 py-2 border-t border-chart-10/20 bg-background/40 rounded-b-lg flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            {configData && (configData.updated_at || configData.updated_by) ? (
              <ConfigMetaBadge
                variant="provenance"
                updatedAt={configData.updated_at}
                updatedBy={configData.updated_by}
              />
            ) : (
              <p className="text-xs text-muted-foreground">No saves recorded yet</p>
            )}
          </div>
          <ConfigChangeHistoryButton kind="utilisation" />
        </div>
      ) : null}
    </div>
  );
}
